import React from "react";
import { Link, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { setchangetomydukan, setchangetoskh } from "../../redux/features/sidebarshift";
import UserDropdown from "components/Dropdowns/UserDropdown.js";

/* ─── Navigation Definitions ───────────────────────────────────────── */

const SCHEMESBOOK_SECTIONS = [
  {
    label: "Catalog",
    items: [
      { title: "Brands",    path: "/admin/brands",    icon: "fas fa-tag" },
      { title: "Retailer",  path: "/admin/retailer",  icon: "fas fa-store" },
      { title: "Products",  path: "/admin/products",  icon: "fas fa-box" },
      { title: "Inventory", path: "/admin/inventory", icon: "fas fa-warehouse" },
    ],
  },
  {
    label: "Pricing & Schemes",
    items: [
      { title: "Schemes",        path: "/admin/schemes",       icon: "fas fa-receipt" },
      { title: "Schemes V2",     path: "/admin/schemesv2",     icon: "fas fa-file-alt" },
      { title: "Schemes V3",     path: "/admin/schemesv3",     icon: "fas fa-cubes" },
      { title: "NLC",            path: "/admin/nlc",           icon: "fas fa-barcode" },
      { title: "NLC V2",         path: "/admin/nlc-v2",        icon: "fas fa-qrcode" },
      { title: "NLC Calculator", path: "/admin/nlcCalculator", icon: "fas fa-calculator" },
      { title: "Price Drop",     path: "/admin/price-drop",    icon: "fas fa-percent" },
    ],
  },
  {
    label: "Content",
    items: [
      { title: "Posts", path: "/admin/posts",  icon: "fas fa-newspaper" },
      { title: "Share", path: "/admin/share",  icon: "fas fa-share-alt" },
      { title: "Files", path: "/admin/files",  icon: "fas fa-folder-open" },
    ],
  },
  {
    label: "Geography",
    items: [
      { title: "States",   path: "/admin/states",    icon: "fas fa-map" },
      { title: "District", path: "/admin/district",  icon: "fas fa-map-marker-alt" },
    ],
  },
  {
    label: "Users & Access",
    items: [
      { title: "Users",              path: "/admin/users",             icon: "fas fa-users" },
      { title: "Groups",             path: "/admin/groups",            icon: "fas fa-layer-group" },
      { title: "Subscriptions",      path: "/admin/subscriptions",     icon: "fas fa-credit-card" },
      { title: "Bulk Subscription",  path: "/admin/bulkSubscription",  icon: "fas fa-users-cog" },
      { title: "TOTP",               path: "/admin/totp",              icon: "fas fa-shield-alt" },
    ],
  },
  {
    label: "Communications",
    items: [
      { title: "Notifications",     path: "/admin/notifications",       icon: "fas fa-bell" },
      { title: "Notification Topic",path: "/admin/notification-topics", icon: "fas fa-tags" },
      { title: "Whatsapp",          path: "/admin/whatsapp",            icon: "fab fa-whatsapp" },
      { title: "Connect",           path: "/admin/connect",             icon: "fas fa-headset" },
    ],
  },
  {
    label: "Insights",
    items: [
      { title: "Analytics", path: "/admin/analytics", icon: "fas fa-chart-line" },
    ],
  },
];

const MYDUKAN_SECTIONS = [
  {
    label: "Growbiz",
    items: [
      { title: "Products",         path: "/admin/myDukanproducts",    icon: "fas fa-box-open" },
      { title: "Brands",           path: "/admin/myDukanbrands",      icon: "fas fa-tag" },
      { title: "Price Drop",       path: "/admin/myDukanpricedrop",   icon: "fas fa-percent" },
      { title: "HSN",              path: "/admin/myDukanhsn",         icon: "fas fa-hashtag" },
      { title: "Block / Unblock",  path: "/admin/myDukanblocknunblock", icon: "fas fa-ban" },
    ],
  },
  {
    label: "Users & Subscriptions",
    items: [
      { title: "Subscriptions", path: "/admin/mydukanSubscriptions", icon: "fas fa-credit-card" },
      { title: "Users",         path: "/admin/mydukanUsers",         icon: "fas fa-users" },
    ],
  },
];

/* Map path segments to human-readable page titles */
const PAGE_TITLES = {
  brands: "Brands", retailer: "Retailer Types", products: "Products",
  inventory: "Inventory", schemes: "Schemes", schemesv2: "Schemes V2", schemesv3: "Schemes V3",
  nlc: "NLC", "nlc-v2": "NLC V2", nlcCalculator: "NLC Calculator",
  "price-drop": "Price Drop", posts: "Posts", share: "Share", files: "Files",
  states: "States", district: "District", users: "Users", groups: "Groups",
  subscriptions: "Subscriptions", bulkSubscription: "Bulk Subscription",
  totp: "TOTP", notifications: "Notifications",
  "notification-topics": "Notification Topics", whatsapp: "WhatsApp",
  connect: "Connect", analytics: "Analytics",
  myDukanhsn: "HSN", myDukanbrands: "Growbiz Brands",
  myDukanproducts: "Growbiz Products", myDukanpricedrop: "Growbiz Price Drop",
  myDukanblocknunblock: "Block / Unblock",
  mydukanSubscriptions: "Growbiz Subscriptions", mydukanUsers: "Growbiz Users",
};

/* ─── NavItem ───────────────────────────────────────────────────────── */

function NavItem({ item, onClick }) {
  const location = useLocation();
  const isActive = location.pathname.startsWith(item.path);

  return (
    <li>
      <Link
        to={item.path}
        onClick={onClick}
        className={`sb-nav-item${isActive ? " active" : ""}`}
      >
        <i className={`${item.icon} sb-icon`} />
        <span>{item.title}</span>
      </Link>
    </li>
  );
}

/* ─── Sidebar ───────────────────────────────────────────────────────── */

export default function Sidebar() {
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const sideVal   = useSelector((state) => state.sidereducer.sideval);
  const dispatch  = useDispatch();
  const location  = useLocation();

  const sections = sideVal === "mydukan" ? MYDUKAN_SECTIONS : SCHEMESBOOK_SECTIONS;
  const brandLabel = sideVal === "mydukan" ? "Growbiz Admin" : "SB Admin";

  const segment   = location.pathname.split("/").filter(Boolean).pop();
  const pageTitle = PAGE_TITLES[segment] ?? "Dashboard";

  const closeMobile = () => setMobileOpen(false);

  return (
    <>
      {/* ── Mobile overlay ── */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 md:hidden"
          onClick={closeMobile}
        />
      )}

      {/* ── Sidebar panel ── */}
      <nav
        className={`
          fixed top-0 left-0 bottom-0 flex flex-col w-64 z-40
          transition-transform duration-300 ease-in-out
          ${mobileOpen ? "translate-x-0" : "-translate-x-full"}
          md:translate-x-0
        `}
        style={{ backgroundColor: "var(--color-sidebar-bg)" }}
      >
        {/* Brand */}
        <div className="flex items-center justify-between px-5 h-16 flex-shrink-0"
          style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}
        >
          <Link to="/" className="flex items-center gap-2" onClick={closeMobile}>
            <span className="w-7 h-7 rounded-lg flex items-center justify-center text-white text-xs font-bold"
              style={{ background: "var(--color-sidebar-active)" }}>
              SB
            </span>
            <span className="text-white font-bold text-sm tracking-wide">{brandLabel}</span>
          </Link>
          <button
            className="md:hidden text-slate-400 hover:text-white transition-colors"
            onClick={closeMobile}
          >
            <i className="fas fa-times" />
          </button>
        </div>

        {/* Platform switcher */}
        <div className="px-4 py-3 flex-shrink-0"
          style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}
        >
          <p className="text-xs font-semibold uppercase tracking-widest mb-2"
            style={{ color: "#475569" }}>
            Platform
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => dispatch(setchangetomydukan())}
              className="flex-1 py-1.5 rounded-md text-xs font-semibold transition-all duration-150"
              style={
                sideVal === "mydukan"
                  ? { background: "rgba(99,102,241,0.2)", color: "#A5B4FC" }
                  : { background: "rgba(255,255,255,0.05)", color: "#94A3B8" }
              }
            >
              Growbiz
            </button>
            <button
              onClick={() => dispatch(setchangetoskh())}
              className="flex-1 py-1.5 rounded-md text-xs font-semibold transition-all duration-150"
              style={
                sideVal !== "mydukan"
                  ? { background: "rgba(99,102,241,0.2)", color: "#A5B4FC" }
                  : { background: "rgba(255,255,255,0.05)", color: "#94A3B8" }
              }
            >
              Schemesbook
            </button>
          </div>
        </div>

        {/* Nav list */}
        <div className="flex-1 overflow-y-auto py-3 px-3 sb-scroll">
          {sections.map((section) => (
            <div key={section.label}>
              <p className="sb-section-label">{section.label}</p>
              <ul className="space-y-0.5">
                {section.items.map((item) => (
                  <NavItem key={item.path} item={item} onClick={closeMobile} />
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom user row */}
        <div className="px-4 py-3 flex-shrink-0 flex items-center gap-3"
          style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}
        >
          <div className="w-8 h-8 rounded-full bg-indigo-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
            A
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-white truncate">Administrator</p>
            <p className="text-xs truncate" style={{ color: "#64748B" }}>SRR Enterprise</p>
          </div>
        </div>
      </nav>

      {/* ── Mobile top bar (hamburger + page title + gradient) — fixed so it's outside flex flow ── */}
      <div
        className="md:hidden flex items-center justify-between px-4 h-14 fixed top-0 left-0 right-0 z-20"
        style={{
          background: "linear-gradient(135deg, var(--color-header-from) 0%, var(--color-header-to) 100%)",
          boxShadow: "0 2px 8px rgba(79,70,229,0.3)",
        }}
      >
        <div className="flex items-center gap-3">
          <button
            className="p-1.5 rounded-md text-white/80 hover:text-white hover:bg-white/10 transition-colors"
            onClick={() => setMobileOpen(true)}
          >
            <i className="fas fa-bars text-lg" />
          </button>
          <div className="flex flex-col leading-tight">
            <span className="font-bold text-xs text-white/60 uppercase tracking-wider">{brandLabel}</span>
            <span className="font-bold text-sm text-white leading-tight">{pageTitle}</span>
          </div>
        </div>
        <UserDropdown />
      </div>
    </>
  );
}

